import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material'
import { inject } from 'mobx-react'
import moment from 'moment'
import React from 'react'
import { IEpisodeOfCare } from 'src/Definitions'
import { IStores } from 'src/Stores'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { getMinimumNextQuoteStartDate } from 'src/services/EpisodeOfCareService'
import { processAction, processDynamicAction } from 'src/services/WorkflowService'
import { QuoteType } from 'src/utils/QuoteType'
import { QuoteDTO } from 'src/viewModels/quotes/QuoteDTO'
import { AdditionalQuoteSummary } from 'src/viewModels/workflow/AdditionalQuoteSummary'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'

interface ICreateAdditionalQuoteModalProps {
  closeModal: () => void
  isOpen: boolean
  reloadParent?: () => void
  action: InboxActionSummary
  isDynamicItem: boolean
  inboxItemId: number
  rerouteToPath?: (path: string) => void
  associatedItemId: string
  inboxId: string
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  data: ObjectiveProcessAction
  selectedQuote?: QuoteDTO
  selectedEpisodeOfCare?: IEpisodeOfCare
}

class CreateAdditionalQuoteModal extends React.Component<ICreateAdditionalQuoteModalProps> {
  public state = {
    loading: true,
    executed: false,
    comments: '',
    bundleDurationDays: undefined,
    startDate: undefined,
    quoteType: undefined,
  }

  public quoteType = [
    { key: QuoteType.NonSurgical.toString(), text: 'Non-Surgical' },
    { key: QuoteType.PostInjection.toString(), text: 'Post-Injection' },
    { key: QuoteType.PostSurgical.toString(), text: 'Post-Surgical' },
  ]

  componentDidMount(): void {
    getMinimumNextQuoteStartDate(this.props.selectedQuote?.episodeOfCareId!).then(
      (result) => {
        this.setNumberOfDays = this.setNumberOfDays.bind(this)
        this.setStartDate = this.setStartDate.bind(this)

        this.setState({
          loading: false,
          startDate: moment(result).format('yyyy-MM-DD'),
        })
      }
    )
  }

  private setNumberOfDays(event: any) {
    this.setState({ bundleDurationDays: event.target.value })
  }

  private setStartDate(event: any) {
    this.setState({ startDate: event.target.value })
  }

  private setQuoteType = (event: any) => {
    this.setState({
      quoteType: event.target.value,
    })
  }

  public render() {
    const { isOpen, associatedItemId } = this.props

    return (
      <Dialog key={associatedItemId} fullWidth={true} maxWidth="sm" open={isOpen!}>
        {this.state.loading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <DialogTitle style={{ padding: 0 }}>
          <ResponsiveAppBar title={this.props.action.actionName} isModal={true} />
        </DialogTitle>
        <DialogContent>
          <Grid item container direction="column" xs={12} style={{ paddingTop: '20px' }}>
            <form>
              <Grid
                container
                item
                direction="row"
                xs={12}
                spacing={2}
                style={{ paddingBottom: '10px' }}
              >
                <Grid item sm={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel shrink={true} variant="outlined">
                      Quote Type
                    </InputLabel>
                    <Select
                      variant="outlined"
                      input={<OutlinedInput notched label="Quote Type" />}
                      fullWidth
                      id="quoteType"
                      required
                      onChange={this.setQuoteType}
                      native={true}
                    >
                      <option value="" key="empty">
                        Select a quote type
                      </option>
                      {this.quoteType?.map((type) => (
                        <option value={type.key} key={type.key}>
                          {type.text}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item sm={4}>
                  <TextField
                    inputProps={{
                      id: 'startDate',
                      style: { padding: '8px' },
                    }}
                    InputLabelProps={{ shrink: true }}
                    onChange={this.setStartDate}
                    type="date"
                    name="startDate"
                    variant="outlined"
                    value={this.state.startDate}
                    label="Start Date"
                    fullWidth
                  />
                </Grid>
                <Grid item sm={3}>
                  <FormControl fullWidth>
                    <TextField
                      inputProps={{ id: 'bundleDurationDays' }}
                      InputLabelProps={{ shrink: true }}
                      onChange={this.setNumberOfDays}
                      name="bundleDurationDays"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={this.state.bundleDurationDays}
                      label="# of Days"
                      required
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item container direction="row" xs={12}>
                {this.props.action.displayComment && (
                  <Grid item xs={12}>
                    <TextField
                      label="Comments"
                      name="notes"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required={this.props.action.requireComment}
                      multiline
                      minRows="4"
                      onChange={(event) =>
                        this.setState({ comments: event.target.value })
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </form>
          </Grid>
        </DialogContent>
        <DialogActions style={{ display: 'flex' }}>
          <Button
            onClick={this.save}
            color="primary"
            variant="contained"
            type="submit"
            disabled={
              this.state.executed ||
              (this.props.action.requireComment && this.state.comments === '') ||
              this.state.bundleDurationDays == '' ||
              this.state.bundleDurationDays == undefined
            }
          >
            Execute
          </Button>
          <Button onClick={this.close} data-cy="close">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
  public secondFormRef = React.createRef<any>()

  private close = () => {
    this.setState({
      loading: true,
      executed: false,
      comments: '',
      bundleDurationDays: undefined,
    })
    this.props.closeModal!()
  }

  private save = async () => {
    this.setState({ loading: true, executed: true })

    var action = this.props.action
    let data = new ObjectiveProcessAction()
    let additionalQuoteSummary = new AdditionalQuoteSummary()

    additionalQuoteSummary.IncludedNonBilledOutClaims = true
    additionalQuoteSummary.quoteType = this.state.quoteType! as QuoteType
    additionalQuoteSummary.startDate = this.state.startDate!
    additionalQuoteSummary.numberOfDays = this.state.bundleDurationDays!

    data.comment = this.state.comments
    data.additionalQuoteData = additionalQuoteSummary

    if (
      this.state.bundleDurationDays == '' ||
      this.state.bundleDurationDays == undefined ||
      (action.requireComment && data.comment == '')
    ) {
      this.setState({ requiredMissing: true })
      return
    }

    if (this.props.isDynamicItem) {
      processDynamicAction(
        data,
        this.props.inboxId.toString(),
        action!.id.toString(),
        this.props.associatedItemId.toString()
      )
        .then(() => {
          this.props.handleProcessActionFinish!(action)
          this.props.closeModal()
        })
        .catch(() => {
          this.setState({ executed: false, loading: false })
        })
    } else {
      processAction(data, action!.id.toString(), this.props.inboxItemId.toString())
        .then(() => {
          this.props.handleProcessActionFinish!(action)
          this.props.closeModal()
        })
        .catch(() => {
          this.setState({ executed: false, loading: false })
        })
    }
  }
}

const InjectedCreateAdditionalQuoteModal = inject<
  IStores,
  ICreateAdditionalQuoteModalProps,
  Partial<ICreateAdditionalQuoteModalProps>,
  any
>((stores: IStores) => ({
  selectedQuote: stores.quotes.selectedQuote,
  selectedEpisodeOfCare: stores.patientEdit.selectedEpisodeOfCare,
}))(CreateAdditionalQuoteModal)

export default InjectedCreateAdditionalQuoteModal
